import { useCallback, useEffect, useState } from "react";
import {
  ApiError,
  providerApi,
  type ProviderBooking,
  type ProviderVenue,
} from "../../lib/api";
import {
  subscribeRealtimeConnection,
  subscribeRealtimeEvent,
} from "../../lib/realtime";
import { useAppPreferences } from "../../lib/preferences";

const POLL_INTERVAL_MS = 30000;

function getErrorDetail(error: unknown, fallback: string) {
  if (error instanceof ApiError) {
    return error.detail;
  }
  return fallback;
}

export function useIncomingBookings(selectedVenue: ProviderVenue | null) {
  const { t } = useAppPreferences();
  const [bookings, setBookings] = useState<ProviderBooking[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!selectedVenue) {
      setBookings([]);
      setError(null);
      setIsLoading(false);
      setHasLoaded(false);
      return;
    }

    // Background refreshes keep the current list on screen; only the first load shows loading.
    setIsLoading(true);

    try {
      const response = await providerApi.listBookings({
        venueId: selectedVenue.id,
        statuses: ["pending", "confirmed"],
      });
      const now = new Date().toISOString();
      const upcoming = response.bookings
        .filter((b) => b.scheduled_end_at > now)
        .sort((a, b) =>
          a.scheduled_start_at.localeCompare(b.scheduled_start_at),
        )
        .slice(0, 6);
      setBookings(upcoming);
      setError(null);
      setHasLoaded(true);
    } catch (requestError) {
      setError(getErrorDetail(requestError, t("Unable to load bookings.")));
    } finally {
      setIsLoading(false);
    }
  }, [selectedVenue, t]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // Only booking changes affect this list; food orders and sessions don't.
  useEffect(() => subscribeRealtimeEvent((event) => {
    if (event.type === "booking.created" || event.type === "booking.updated" || event.type === "resync") {
      void reload();
    }
  }), [reload]);

  useEffect(() => {
    return subscribeRealtimeConnection((isConnected) => {
      if (isConnected) {
        void reload();
      }
    });
  }, [reload]);

  useEffect(() => {
    if (!selectedVenue) {
      return;
    }

    const intervalId = setInterval(() => {
      void reload();
    }, POLL_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [reload, selectedVenue]);

  return { bookings, isLoading, isInitialLoading: isLoading && !hasLoaded, error, reload };
}
